// Noise Lab — the superadmin development space for noise detection. Step 1: training sets, one per
// book, each holding the book's recordings from before and after the edit.
import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { LabSet } from "../api/types";
import { useMe } from "../lib/me";
import { LabTabs } from "../components/LabTabs";

export function hoursMinutes(seconds: number): string {
  if (!seconds) return "—";
  const m = Math.round(seconds / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min`;
}

export function NoiseLab() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const sets = useQuery({
    queryKey: ["lab-sets"],
    queryFn: () => api<{ sets: LabSet[] }>("/api/modules/noise_lab/sets"),
    enabled: !!me?.is_superadmin,
    refetchInterval: (q) => (q.state.data?.sets.some((s) => s.n_waiting > 0) ? 5000 : false),
  });
  const create = useMutation({
    mutationFn: (n: string) => api<LabSet>("/api/modules/noise_lab/sets", { method: "POST", body: JSON.stringify({ name: n }) }),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ["lab-sets"] });
      nav(`/admin/noise-lab/${s.id}`);
    },
  });

  if (me && !me.is_superadmin) return <Navigate to="/" replace />;
  const list = sets.data?.sets ?? [];

  return (
    <div className="page admin-page lab-page">
      <LabTabs />
      <div className="page-head">
        <div>
          <h1>Noise Lab</h1>
          <div className="sub">
            Training material for noise detection. Each set is one book: its recordings from before the edit
            and after it. The lab pairs the chapters and finds every edit that was made.
          </div>
        </div>
      </div>

      <form
        className="card lab-new"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate(name.trim());
        }}
      >
        <label className="lab-new-label" htmlFor="lab-new-name">New set</label>
        <input
          id="lab-new-name"
          className="input"
          placeholder="Book title"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
        />
        <button className="btn primary" type="submit" disabled={!name.trim() || create.isPending}>
          {create.isPending ? "Creating…" : "Create set"}
        </button>
        {create.isError && <span className="lab-error">{(create.error as Error).message}</span>}
      </form>

      {sets.isLoading ? (
        <div className="muted lab-loading">Loading…</div>
      ) : sets.isError ? (
        <div className="lab-error">Couldn't load the sets: {(sets.error as Error).message}</div>
      ) : list.length === 0 ? (
        <div className="empty-state">No training sets yet. Create one above, then drop in a book's two folders.</div>
      ) : (
        <div className="card lab-tablewrap">
          <table className="table lab-table">
            <thead>
              <tr>
                <th>Set</th>
                <th className="r">Chapters</th>
                <th className="r">Processed</th>
                <th className="r">Edits found</th>
                <th className="r">Pause edits</th>
                <th className="r">Audio processed</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link to={`/admin/noise-lab/${s.id}`} className="lab-setlink">{s.name}</Link>
                    {s.n_waiting > 0 && <span className="lab-pill lab-extracting">{s.n_waiting} in progress</span>}
                    {s.n_failed > 0 && <span className="lab-pill lab-failed">{s.n_failed} failed</span>}
                  </td>
                  <td className="r num">{s.n_pairs}</td>
                  <td className="r num">{s.n_done}</td>
                  <td className="r num">{s.n_edits.toLocaleString()}</td>
                  <td className="r num">{s.n_pause_edits.toLocaleString()}</td>
                  <td className="r num">{hoursMinutes(s.processed_seconds)}</td>
                  <td className="muted">{new Date(s.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
